import { Network } from '@aptos-labs/ts-sdk';
import { createGlobalStore } from '@aries/shared/deps';
import { eventEmitter, useLocalStorageRef } from '@aries/shared/utils';
import { FaucetClient } from 'aptos';
import { useEffect, useRef, useState } from 'react';
import {
  AptosNodeMeta,
  APTOS_NODE_LIST,
  getAptosNodeRequestHeaders,
  MAINNET_OFFICIAL_NODE_NAME,
} from './config';
import { createProvider } from './impl';

export {
  getAptosNodeRequestHeaders,
  MAINNET_APTOS_API_NODE_NAME,
  MAINNET_OFFICIAL_NODE_NAME,
} from './config';
export type { AptosNodeMeta } from './config';
export { MAX_GAS_PER_TX } from './impl';
export { Network };

const NETWORK_CHANGE_KEY = 'AptosProviderChange';
const NETWORK_NAME_KEY = 'last_network_name_1';

export const onNetworkChange = (
  callback: (
    network: AptosNodeMeta,
    provider: ReturnType<typeof useProviderHub>['provider'],
  ) => void,
) => {
  eventEmitter.addListener(NETWORK_CHANGE_KEY, (network, provider) => {
    callback(network, provider);
  });
};

export const [useProviderHub, getProviderHub] = createGlobalStore(() => {
  const [networkNameRef, setLastNetworkName] =
    useLocalStorageRef<string>(NETWORK_NAME_KEY);

  const getProvider = (name?: string) => {
    const meta = APTOS_NODE_LIST.find(v => v.name === name);
    if (!meta) {
      return null;
    }

    const providerConfig: Parameters<typeof createProvider>[0] &
      Pick<AptosNodeMeta, 'apiKey' | 'headers'> = {
      nodeUrl: meta.nodeUrl,
      network: meta.network,
      apiKey: meta.apiKey,
      headers: meta.headers,
    };

    const provider = createProvider(providerConfig);

    const faucetClient = new FaucetClient(meta.nodeUrl, meta.faucetUrl);

    return { faucetClient, provider, meta };
  };

  const [{ provider, faucetClient, meta: currentNetwork }, setProvider] =
    // TODO
    useState(
      () =>
        getProvider(networkNameRef.current) ??
        getProvider(APTOS_NODE_LIST[0].name)!,
    );

  // To make we can get the newest provider immediately
  const providerRef = useRef(provider);
  providerRef.current = provider;

  const changeNetwork = (name: string) => {
    const newProvider = getProvider(name);
    if (newProvider) {
      setProvider(newProvider);
      setLastNetworkName(name);
    }
  };

  useEffect(() => {
    eventEmitter.emit(NETWORK_CHANGE_KEY, currentNetwork, provider);
  }, [currentNetwork, provider]);

  return {
    provider: providerRef.current,
    faucetClient,
    currentNetwork,
    list: APTOS_NODE_LIST,
    changeNetwork,
    env: {
      isTestnet: currentNetwork.network === Network.TESTNET,
      isMainnet: currentNetwork.network === Network.MAINNET,
    },
  };
});

export const findFirstHealthyMainnetNode = async (): Promise<string> => {
  const mainnetNodes = APTOS_NODE_LIST.filter(
    node => node.network === Network.MAINNET,
  );

  for (const node of mainnetNodes) {
    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 5000);

      // eslint-disable-next-line no-await-in-loop
      const response = await fetch(node.pingUrl, {
        method: 'GET',
        headers: getAptosNodeRequestHeaders(node),
        signal: controller.signal,
      });

      clearTimeout(timeout);

      if (response.ok) {
        return node.name;
      }

      if (response.status === 401 || response.status === 403) {
        // eslint-disable-next-line no-console
        console.warn(`Auth failed for node ${node.name}`);
        continue;
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn(`Health check failed for node ${node.name}:`, error);
      continue;
    }
  }
  return MAINNET_OFFICIAL_NODE_NAME;
};
